// A technician's time problems (timesheet rules §3, §6, §7): found on a time.day day, and handed to
// the board's own words for them (TechDayCard's problemCardWords, problemLaneWords, problemsNoteWords).
import { TIME_FLAG_WORDS } from '@flowology/domain';
import type { TimeDay, TimeEntry } from './timesheet-types.ts';
import type { TimeProblem } from '@flowology/ui-web/fl';

export {
  problemCardWords,
  problemLaneWords,
  problemsNoteWords,
  type TimeProblem,
} from '@flowology/ui-web/fl';

/** A flag's code from the server's words (time.day says the words, not the code). */
export function flagCode(words: string | null): string | null {
  if (!words) return null;
  return Object.entries(TIME_FLAG_WORDS).find(([, w]) => w === words)?.[0] ?? 'other';
}

/** What's wrong with one day, the likeliest-to-fix first. */
export type DayProblem =
  /** A part left open on a day that's over: a missed clock-out. */
  | { kind: 'missed'; entry: TimeEntry }
  /** Every part ended but the day never was (no End day); never a day the import brought in whole. */
  | { kind: 'notEnded'; lastEnd: number }
  /** A part the server flagged. */
  | { kind: 'flag'; entry: TimeEntry; code: string; words: string };

/**
 * A part's check, in the server's words: only an ended part has one. time.day words a part that never
 * ended "Still open"; that's the missed clock-out (or running now), drawn as such, not a second problem.
 */
export const checkOf = (e: TimeEntry): string | null => (e.endMinute !== null ? e.flag : null);

export function dayProblems(day: TimeDay, today: string): DayProblem[] {
  const out: DayProblem[] = [];
  const open = day.entries.filter((e) => e.endMinute === null && !e.running);
  for (const e of open) out.push({ kind: 'missed', entry: e });
  const last = day.entries.at(-1);
  if (
    !open.length &&
    last &&
    // time brought in from the earlier system has no End my day to miss (QA-760)
    !day.entries.every((e) => e.imported) &&
    last.endMinute !== null &&
    day.date < today &&
    day.endTrigger === null &&
    !day.entries.some((e) => e.running)
  )
    out.push({ kind: 'notEnded', lastEnd: last.endMinute });
  // one problem, one row (checkOf): as time.team counts them, flagged is ended, flagged, not settled
  for (const e of day.entries) {
    const words = checkOf(e);
    if (words) out.push({ kind: 'flag', entry: e, code: flagCode(words)!, words });
  }
  return out;
}

/** The board's shape of a day's problems (time.teamDay), for the card and lane words. */
export function asTimeProblems(day: TimeDay, today: string): TimeProblem[] {
  return dayProblems(day, today).map((p) =>
    p.kind === 'flag' ? { kind: 'checkTime', date: day.date } : { kind: 'noClockOut', date: day.date },
  );
}
